import type { ComputedRef, Ref } from 'vue';
import { computed, readonly, ref } from 'vue';
import { del, set } from 'vue-demi';
import { type PinnedMap, PinItemType } from '@/types/Pinned';
import { getAllDescendantIds } from '@/utils/portfolioTree';
import { createGlobalState, until } from '@vueuse/core';
import { SettingsModule } from '@/store/modules/settings';
import type { ISidebarRow, ISidebarRowSubportfolio } from '@/types/ISidebarRow';
import { isISidebarRowSubportfolio } from '@/types/ISidebarRow';
import type {
  PinItemParameters,
  PinItemRecursiveParameters,
  PinItemUnderAnalysisParameters,
} from '@/types/PinItemParameters';
import { isPinItemRecursiveParameter, isPinItemWithChildrenParameter } from '@/types/PinItemParameters';
import type { IPortfolioTreeStrategy, IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { isPortfolioTreeStrategy, isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { PortfolioType } from '@/types/portfolio';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { ApplicationDefaults } from '@/constants/ApplicationDefaults';
import { usePortfolioUnderAnalysisAttributesGlobalState } from './usePortfolioUnderAnalysisAttributesGlobalState';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import { useActiveReturnState } from './useAnalysisModes';
import { ActiveReturnMode } from '@/types/ActiveReturnMode';

const useColorMapGlobalState = createGlobalState(
  (): {
    nextPinnedColorIndex: Ref<number>;
    colorMapRaw: Ref<PinnedMap>;
    colorMap: ComputedRef<PinnedMap>;
    itemUnderAnalysisColor: ComputedRef<string>;
    strategyCodeToColor: ComputedRef<{ [strategyCode: string]: string }>;
    hoveredItemId: Ref<string | null>;
    firstMatrixHoveredItemId: Ref<string | null>;
    secondMatrixHoveredItemId: Ref<string | null>;
    allPinnedColorsArray: ComputedRef<string[]>;
    isOnlyItemUnderAnalysisPinned: ComputedRef<boolean>;
  } => {
    const nextPinnedColorIndex = ref(0);

    const colorMapRaw = ref<PinnedMap>({});

    /**
     * Some graphs require at least 1 pinned track other than the item under analysis
     * This is used to show an error message when the graph is rendered without any pinned tracks
     */
    const isOnlyItemUnderAnalysisPinned = computed((): boolean => {
      const values = Object.values(colorMapRaw.value);

      return values.length === 1 && !values.some((item) => item.type !== PinItemType.ITEM_UNDER_ANALYSIS);
    });

    /**
     * A map of strategy codes to their colors in the colorMap
     */
    const strategyCodeToColor = computed((): { [strategyCode: string]: string } => {
      const retval: { [strategyCode: string]: string } = {};
      for (const [, item] of Object.entries(colorMapRaw.value)) {
        if (item.strategyCode) {
          retval[item.strategyCode] = item.color;
        }
      }

      return retval;
    });

    const hoveredItemId = ref<string | null>(null);
    const firstMatrixHoveredItemId = ref<string | null>(null);
    const secondMatrixHoveredItemId = ref<string | null>(null);

    const allPinnedColorsArray = computed((): string[] => Object.values(SettingsModule.colors.pin || {}));

    // We use useActiveReturnState directly instead of useAnalysisModes because:
    // 1. useAnalysisModes requires setup context (it uses useRoute internally)
    // 2. We only need the activeReturnMode state, not the full composable functionality
    // 3. useActiveReturnState is a global state that can be safely used outside of setup
    const { activeReturnMode } = useActiveReturnState();
    const isActiveReturnStateActive = computed(
      (): boolean => activeReturnMode.value === ActiveReturnMode.ACTIVE_RETURN,
    );

    /**
     * Checks if there's any item in the color map that is specifically marked as an active return type.
     *
     * When using Active Return, we either:
     * - show the 'Active Return' line (in the Active Return color), or
     * - compute the itemUnderAnalysis in Active Return (and show that item in the Active Return color)
     *
     * If we are in Active Return and this returns true, then we show the itemUnderAnalysis in `portfolioColor`
     * otherwise we show the itemUnderAnalysis in the Active Return color.
     */
    const hasActiveReturnTypeInColorMap = computed((): boolean =>
      Object.values(colorMapRaw.value).some((pinnedItem) => pinnedItem.type === PinItemType.ACTIVE_RETURN),
    );

    const { isPeerGroupPortfolio } = usePortfolioUnderAnalysisAttributesGlobalState();
    const { shouldTreatRootItemAsPortfolio } = useItemUnderAnalysisGlobalState();

    const itemUnderAnalysisColor = computed((): string => {
      if (hasActiveReturnTypeInColorMap.value) {
        return SettingsModule.portfolioColor;
      }

      if (isActiveReturnStateActive.value) {
        return SettingsModule.activeReturnColor;
      }

      // Right now we don't support Peer Group with active return.
      // But when we do, product team need to decide what color they want to see when peer group is in active return.
      if (isPeerGroupPortfolio.value && shouldTreatRootItemAsPortfolio.value) {
        return ApplicationDefaults.colors.PeerGroup;
      }

      return SettingsModule.portfolioColor;
    });

    /**
     * Read-time view of the pinned map with the item-under-analysis color overlaid.
     * Shared across all useColorMap() callers so sidebar rows do not each rebuild this map.
     */
    const colorMap = computed((): PinnedMap => {
      const source = colorMapRaw.value;
      const newColorMap: PinnedMap = { ...source };

      for (const [key, value] of Object.entries(newColorMap)) {
        if (value.type === PinItemType.ITEM_UNDER_ANALYSIS) {
          newColorMap[key] = {
            ...value,
            color: itemUnderAnalysisColor.value,
          };
          break;
        }
      }

      return newColorMap;
    });

    return {
      nextPinnedColorIndex,
      colorMapRaw,
      colorMap,
      itemUnderAnalysisColor,
      strategyCodeToColor,
      hoveredItemId,
      firstMatrixHoveredItemId,
      secondMatrixHoveredItemId,
      allPinnedColorsArray,
      isOnlyItemUnderAnalysisPinned,
    };
  },
);

/**
 * Analysis Module color logic rules:
 *
 * Portfolio Construction:
 * - only the immediate children of the itemUnderAnalysis will be pinned
 * - if the user pins an item that is not one of the immediate children of the itemUnderAnalysis, this item will be pinned and added to the overlayData (to be shown in the graphs)
 * - strategies may not be made the itemUnderAnalysis, so clicking the Name button of a strategy will just toggle its pinned state
 *
 * Performance Contribution:
 * - When in Performance Contribution substep, the color logic of Performance Contribution will take place instead of the Portfolio Construction logic (above)
 * - All children of the itemUnderAnalysis will be pinned recursively
 * - Only subportfolios may be the itemUnderAnalysis
 * - Clicking the pin button will do nothing
 *
 * Factor Decomposition:
 * - clicking on a Name button or a pin button will make it the itemUnderAnalysis
 * - no other rules, no other items are pinned
 *
 * Risk / Position:
 * - All children of the itemUnderAnalysis will be pinned recursively
 * - Strategies may be the itemUnderAnalysis
 * - If the itemUnderAnalysis is not the master portfolio, and the user clicks the pin button of another row, then the itemUnderAnalysis will automatically become the closest common ancestor to both of those items (keeping them both pinned, but not automatically pinning any others)
 *   - this was previously known as the 'special risk case'
 */
export default function (): {
  hoveredItemId: Ref<string | null>;
  colorMap: Readonly<Ref<PinnedMap>>;
  setColorMap: (map: PinnedMap) => void;
  firstMatrixHoveredItemId: Readonly<Ref<string | null>>;
  secondMatrixHoveredItemId: Readonly<Ref<string | null>>;
  setFirstMatrixHoveredItemId: (id: string | null) => void;
  setSecondMatrixHoveredItemId: (id: string | null) => void;
  setHoveredItemId: (id: string | null) => void;
  /**
   * If `type` is provided, then all items with the given type will be unpinned
   */
  unpin: (key: string, type?: PinItemType) => void;
  getIsUnpinned: (key: string) => boolean;
  pinPortfolioTreeItem: (params: PinItemParameters) => Promise<PinnedMap>;
  /**
   * 'selfKey' refers to the unique key that the item will be identified with,
   *
   * 'selfCode' refers to the strategy code (possibly undefined and possibly non-unique) that is part of the item
   */
  pinItemUnderAnalysis: (params: PinItemUnderAnalysisParameters) => Promise<void>;
  pinBaseFund: (itemUnderAnalysis: IPortfolioTreeSubportfolio) => Promise<PinnedMap> | null;
  pin: ({
    keys,
    forceColor,
    type,
    override,
  }: {
    keys: string[];
    forceColor?: string;
    type?: PinItemType;
    /**
     * If `true`, and if an entry with the same type exists, then pinning will override the existing entry
     *
     * e.g., if you pin an item with type BENCHMARK and override is true, you will replace the existing BENCHMARK entry
     *
     * The use case for this is that in some places we have moved to use the id of a track instead of the strategy code.
     * While we are migrating from code to id, we will pin both for a given strategy.
     *
     * TODO: remove when WAA-11921 is complete
     */
    override?: boolean;
  }) => Promise<void>;
  pinItemAndClearAllOthers: (id: string, type?: PinItemType) => void;
  togglePeerGroupNameButton: ({
    id,
    item,
    itemUnderAnalysis,
  }: {
    id: string;
    item: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
    itemUnderAnalysis: IItemUnderAnalysis | null;
  }) => Promise<void>;
  getPinParameters: ({
    newItemUnderAnalysis,
    childrenToPin,
    idsToSkip,
    areNumberOfItemsToAutoPinOverMax,
  }: {
    newItemUnderAnalysis: IItemUnderAnalysis;
    childrenToPin?: ISidebarRow[];
    idsToSkip?: Set<string>;
    areNumberOfItemsToAutoPinOverMax?: boolean;
  }) => PinItemUnderAnalysisParameters;
  clearPinned: () => Promise<void>;
  strategyCodeToColor: ComputedRef<{ [strategyCode: string]: string }>;
  itemUnderAnalysisColor: ComputedRef<string>;
  isOnlyItemUnderAnalysisPinned: Ref<boolean>;
  getRecursivePinItemParameters: (params: {
    key: string;
    row: ISidebarRow | null;
    idsToSkip: Set<string>;
    justReturnChanges?: boolean;
  }) => PinItemParameters;
  getUnpinSidebarRowChanges: (params: {
    mapToReturn: PinnedMap;
    row: ISidebarRow;
    /**
     * The "root subportfolio" here is the subportfolio that is being unpinned, NOT
     * the master portfolio.
     */
    unpinRootSubportfolio?: boolean;
  }) => PinnedMap;
  allPinnedColorsArray: Ref<string[]>;
  /**
   * This function is intended to be used to generate somewhat distinct colors from the pinned colors in use.
   * For example, `PerformanceContributionStackedBarChart` uses this function to generate colors for the bullets on
   * each bar, which use a separate color palette from the bars themselves.
   */
  getNextPinColorByOffset: (
    idx: number,
    /**
     * Number used to control how many pin colors to skip before returning the next color in the sequence.
     */
    offset?: number,
  ) => string;
} {
  const {
    nextPinnedColorIndex,
    allPinnedColorsArray,
    colorMapRaw,
    colorMap,
    hoveredItemId,
    firstMatrixHoveredItemId,
    secondMatrixHoveredItemId,
    strategyCodeToColor,
    isOnlyItemUnderAnalysisPinned,
    itemUnderAnalysisColor,
  } = useColorMapGlobalState();

  const setFirstMatrixHoveredItemId = (id: string | null): void => {
    firstMatrixHoveredItemId.value = id;
  };

  const setSecondMatrixHoveredItemId = (id: string | null): void => {
    secondMatrixHoveredItemId.value = id;
  };

  const setColorMap = (map: PinnedMap): void => {
    colorMapRaw.value = map;
  };

  const clearPinned = async (): Promise<void> => {
    nextPinnedColorIndex.value = 0;
    setColorMap({});
  };

  const waitForColors = async (): Promise<void> => {
    if (allPinnedColorsArray.value.length) {
      // Already in good shape
      return;
    }

    await until(allPinnedColorsArray).changed({ timeout: 3 * 1000 });
  };

  const getNextPinnedColor = async (): Promise<{ hex: string; index: number }> => {
    if (!allPinnedColorsArray.value.length) {
      await waitForColors();
    }

    if (allPinnedColorsArray.value[nextPinnedColorIndex.value + 1]) {
      nextPinnedColorIndex.value += 1;
    } else nextPinnedColorIndex.value = 0;

    return {
      hex: allPinnedColorsArray.value[nextPinnedColorIndex.value],
      index: nextPinnedColorIndex.value,
    };
  };

  const getColorFromType = (type?: PinItemType): string | undefined => {
    if (type === PinItemType.BENCHMARK || type === PinItemType.PORTFOLIO_INDEX)
      return ApplicationDefaults.colors.Benchmark;

    if (type === PinItemType.ACTIVE_RETURN) return ApplicationDefaults.colors.ActiveReturn;

    if (type === PinItemType.BASE_FUND) return ApplicationDefaults.colors.BaseFund;

    return undefined;
  };

  /**
   * To be used when pinning an item that is strictly not involved with the portfolio tree
   */
  const pin = async ({
    keys,
    forceColor,
    type,
    override = true,
  }: {
    keys: string[];
    forceColor?: string;
    type?: PinItemType;
    override?: boolean;
  }): Promise<void> => {
    const knownColor = forceColor || getColorFromType(type);

    let color: {
      hex: string | undefined;
      pinIndex: number | undefined;
    } = {
      hex: knownColor,
      pinIndex: undefined,
    };

    if (!knownColor) {
      const { hex: nextPinnedColor, index: nextPinnedColorIndex } = await getNextPinnedColor();

      color = {
        hex: nextPinnedColor,
        pinIndex: nextPinnedColorIndex,
      };
    }

    if (type && override) {
      for (const [key, value] of Object.entries(colorMapRaw.value)) {
        if (value.type === type) {
          del(colorMapRaw.value, key);
        }
      }
    }

    const pinFn = (key: string, type?: PinItemType): void => {
      set(colorMapRaw.value, key, {
        color: color.hex,
        thickness: 1,
        type,
        pinIndex: color.pinIndex,
      });
    };

    for (const key of keys) {
      pinFn(key, type);
    }
  };

  /**
   * Peer group portfolios include a feature to only pin an item and unpin everything else when its NameButton is clicked
   *
   * @param id the id of the peer group item to be selected
   */
  const pinItemAndClearAllOthers = async (id: string, type?: PinItemType): Promise<void> => {
    const changesToMake = await pinPortfolioTreeItem({ selfKey: id, type, justReturnChanges: true });
    setColorMap(changesToMake);
  };

  /**
   * If there is a strategy code for the item and we're not forcing a new color,
   * then we return the color of the strategy code if it exists in the colorMap
   */
  const getStrategyColor = ({
    strategyCode,
    forceNewColorForStrategy,
  }: {
    strategyCode?: string;
    forceNewColorForStrategy?: boolean;
  }): string | null => {
    if (strategyCode && !forceNewColorForStrategy) {
      return strategyCodeToColor.value[strategyCode] ?? null;
    }

    return null;
  };

  /**
   * To be used when pinning an item that is involved with the portfolio tree
   * This will use the color of the existing item in the colormap, so that an item retains its colour
   */
  const pinPortfolioTreeItem = async (
    params: PinItemParameters & {
      /**
       * If true, the function will force a new color for the strategy code,
       * even if it already exists in the colorMap. This is mostly used in regenerating the colorMap.
       */
      forceNewColorForStrategy?: boolean;
    },
  ): Promise<PinnedMap> => {
    let changesToMake: PinnedMap = {};

    const strategyColor = getStrategyColor({
      strategyCode: params.selfCode,
      forceNewColorForStrategy: params.forceNewColorForStrategy,
    });

    const colorToUse =
      params.forceColor ?? strategyColor ?? getColorFromType(params.type) ?? (await getNextPinnedColor()).hex;
    changesToMake[params.selfKey] = {
      strategyCode: params.selfCode,
      color: colorToUse,
      thickness: 1,
    };

    if (isPinItemWithChildrenParameter(params)) {
      for (const child of params.children ?? []) {
        if (params.idsToSkip?.has(child.portfolioTreeId)) continue;

        const changesFromChild = await pinPortfolioTreeItem({
          selfKey: child.portfolioTreeId,
          selfCode: !isISidebarRowSubportfolio(child) ? child.code : undefined,
          justReturnChanges: params.justReturnChanges,
        });

        changesToMake = {
          ...changesToMake,
          ...changesFromChild,
        };
      }
    }

    if (isPinItemRecursiveParameter(params)) {
      const descendants = getAllDescendantIds({
        subportfolioRow: params.subportfolioRowToRecurse,
      }).filter(
        (x) =>
          // don't pin inactive items
          !params.idsToSkip.has(x.portfolioTreeId) &&
          // and don't pin immediate children, which we handle above separately
          // (see note on `children` on `PinItemRecursiveParameters`)
          !params.children.map((child) => child.portfolioTreeId).includes(x.portfolioTreeId),
      );
      for (const descendant of descendants) {
        const changesFromDescendant = await pinPortfolioTreeItem({
          selfKey: descendant.portfolioTreeId,
          selfCode: !isISidebarRowSubportfolio(descendant) ? descendant.code : undefined,
          justReturnChanges: params.justReturnChanges,
        });

        changesToMake = {
          ...changesToMake,
          ...changesFromDescendant,
        };
      }
    }

    if (!params.justReturnChanges) {
      for (const [key, value] of Object.entries(changesToMake)) {
        set(colorMapRaw.value, key, value);
      }
    }

    return changesToMake;
  };

  /**
   * To be used when pinning the item under analysis
   * The parent key will be the item under analysis and the descendants will color accordingly
   *
   * NOTE: when we pin the item under analysis, most of the time we want to reset the colorMap entirely.
   * If you don't want to reset the colorMap, you can set `appendChanges` to `true`
   */
  const pinItemUnderAnalysis = async (params: PinItemUnderAnalysisParameters): Promise<void> => {
    // if we're not appending changes, we should reset the nextPinnedColorIndex back to 0
    if (!params.appendChanges) {
      nextPinnedColorIndex.value = 0;
    }

    let changesToMake: PinnedMap = {
      [params.selfKey]: {
        color: params.forceColor ?? SettingsModule.portfolioColor,
        strategyCode: params.selfCode,
        thickness: 2,
        type: PinItemType.ITEM_UNDER_ANALYSIS,
      },
    };

    if (isPinItemWithChildrenParameter(params)) {
      for (const child of params.children ?? []) {
        if (params.idsToSkip?.has(child.portfolioTreeId)) continue;

        const pinParams = isISidebarRowSubportfolio(child)
          ? {
              selfKey: child.portfolioTreeId,
              justReturnChanges: true,
              forceNewColorForStrategy: true,
            }
          : {
              selfKey: child.portfolioTreeId,
              selfCode: child.code,
              type: child.type,
              justReturnChanges: true,
              forceNewColorForStrategy: true,
            };

        const changesFromChild = await pinPortfolioTreeItem(pinParams);

        changesToMake = {
          ...changesToMake,
          ...changesFromChild,
        };
      }
    }

    if (isPinItemRecursiveParameter(params)) {
      const descendants = getAllDescendantIds({
        subportfolioRow: params.subportfolioRowToRecurse,
      }).filter(
        (x) =>
          // don't pin inactive items
          !params.idsToSkip.has(x.portfolioTreeId) &&
          // and don't pin immediate children, which we handle above separately
          // (see note on `children` on `PinItemRecursiveParameters`)
          !params.children.map((child) => child.portfolioTreeId).includes(x.portfolioTreeId),
      );

      for (const descendant of descendants) {
        const changesFromDescendant = await pinPortfolioTreeItem({
          selfKey: descendant.portfolioTreeId,
          selfCode: !isISidebarRowSubportfolio(descendant) ? descendant.code : undefined,
          justReturnChanges: true,
          forceNewColorForStrategy: true,
        });

        changesToMake = {
          ...changesToMake,
          ...changesFromDescendant,
        };
      }
    }

    if (params.appendChanges) {
      changesToMake = {
        ...colorMapRaw.value,
        ...changesToMake,
      };

      // if we're just appending changes, we need to make sure to remove the previous entry
      // for the item under analysis
      if (params.previousItemUnderAnalysisId) {
        delete changesToMake[params.previousItemUnderAnalysisId];
      }
    }

    setColorMap(changesToMake);
  };

  const pinBaseFund = (itemUnderAnalysis: IPortfolioTreeSubportfolio): Promise<PinnedMap> | null => {
    const baseFundComponent = itemUnderAnalysis.components
      .filter(isPortfolioTreeStrategy)
      .find((o) => o.portfolioTreeId === itemUnderAnalysis.baseFund);
    if (!baseFundComponent) return null;

    return pinPortfolioTreeItem({
      selfKey: baseFundComponent.portfolioTreeId,
      selfCode: baseFundComponent.strategy?.code,
      type: PinItemType.BASE_FUND,
    });
  };

  const unpin = (originalKey: string, type?: PinItemType): void => {
    const keys = type
      ? Object.entries(colorMapRaw.value)
          .filter(([, entry]) => entry.type === type)
          .map(([key]) => key)
      : [originalKey];

    for (const key of keys) {
      if (colorMapRaw.value[key]) {
        del(colorMapRaw.value, key);
      }
    }
  };

  const setHoveredItemId = (id: string | null): void => {
    hoveredItemId.value = id;
  };

  const getRecursivePinItemParameters = ({
    key,
    row,
    idsToSkip,
    justReturnChanges,
  }: {
    key: string;
    row: ISidebarRow | null;
    idsToSkip: Set<string>;
    justReturnChanges?: boolean;
  }): PinItemParameters => {
    if (row && isISidebarRowSubportfolio(row)) {
      const retval: PinItemRecursiveParameters = {
        selfKey: key,
        children: row.children,
        subportfolioRowToRecurse: row,
        idsToSkip,
        justReturnChanges,
      };

      return retval;
    }

    return {
      selfKey: key,
      justReturnChanges,
    };
  };

  /**
   * In order to avoid changing the colorMap an excessive number of times,
   * this function will return the changes to make to the colorMap,
   * leaving it up to the caller to apply the changes to the colorMap.
   */
  const getUnpinSidebarRowChanges = ({
    mapToReturn,
    row,
    unpinRootSubportfolio = true,
  }: {
    mapToReturn: PinnedMap;
    row: ISidebarRow;
    /**
     * The "root subportfolio" here is the subportfolio that is being unpinned, NOT
     * the master portfolio.
     */
    unpinRootSubportfolio?: boolean;
  }): PinnedMap => {
    if (unpinRootSubportfolio) delete mapToReturn[row.portfolioTreeId];

    if (isISidebarRowSubportfolio(row)) {
      mapToReturn = getUnpinAllChildRowChanges({ mapToReturn, row });
    }

    return mapToReturn;
  };

  const getUnpinAllChildRowChanges = ({
    row,
    mapToReturn,
  }: {
    row: ISidebarRowSubportfolio;
    mapToReturn: PinnedMap;
  }): PinnedMap => {
    for (const child of row.children) {
      delete mapToReturn[child.portfolioTreeId];

      if (isISidebarRowSubportfolio(child) && child.children.length) {
        getUnpinAllChildRowChanges({ row: child, mapToReturn });
      }
    }

    return mapToReturn;
  };

  const getIsUnpinned = (id: string): boolean => !colorMapRaw.value[id];

  const getPinParameters = ({
    newItemUnderAnalysis,
    childrenToPin = [],
    idsToSkip = new Set<string>(),
    areNumberOfItemsToAutoPinOverMax = false,
  }: {
    newItemUnderAnalysis: IItemUnderAnalysis;
    childrenToPin?: ISidebarRow[];
    idsToSkip?: Set<string>;
    areNumberOfItemsToAutoPinOverMax?: boolean;
  }): PinItemParameters => {
    const selfKey = isStrategyFactsheetResponseDTO(newItemUnderAnalysis)
      ? newItemUnderAnalysis.strategy.code
      : newItemUnderAnalysis.portfolioTreeId;
    const selfCode = isPortfolioTreeStrategy(newItemUnderAnalysis) ? newItemUnderAnalysis.strategy?.code : undefined;

    if (areNumberOfItemsToAutoPinOverMax) {
      return {
        selfKey,
      };
    }

    return {
      selfKey,
      selfCode,
      children: childrenToPin,
      idsToSkip,
    };
  };

  /**
   * If the item is the item under analysis, we want to pin the item under analysis and base fund if it has one.
   * Otherwise, we want to clear all other pins and pin the item.
   */
  const togglePeerGroupNameButton = async ({
    id,
    item,
    itemUnderAnalysis,
  }: {
    id: string;
    item: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
    itemUnderAnalysis: IItemUnderAnalysis | null;
  }): Promise<void> => {
    if (itemUnderAnalysis && isStrategyFactsheetResponseDTO(itemUnderAnalysis)) return;

    // if base fund is clicked
    if (itemUnderAnalysis && itemUnderAnalysis.type === PortfolioType.PEER_GROUP && id === itemUnderAnalysis.baseFund) {
      pinItemAndClearAllOthers(id, PinItemType.BASE_FUND);
      return;
    }

    if (
      itemUnderAnalysis &&
      !isStrategyFactsheetResponseDTO(item) &&
      item.portfolioTreeId === id &&
      isPortfolioTreeSubportfolio(item)
    ) {
      // pin the item under analysis
      await pinItemUnderAnalysis(getPinParameters({ newItemUnderAnalysis: itemUnderAnalysis, childrenToPin: [] }));

      // If the item under analysis has a base fund, pin it
      if (itemUnderAnalysis.type === PortfolioType.PEER_GROUP && itemUnderAnalysis.baseFund) {
        pin({
          keys: [itemUnderAnalysis.baseFund],
          type: PinItemType.BASE_FUND,
        });
      }

      return;
    }

    pinItemAndClearAllOthers(id);
  };

  const getNextPinColorByOffset = (idx: number, offset: number = 10): string => {
    return allPinnedColorsArray.value.length
      ? allPinnedColorsArray.value[(idx + offset) % allPinnedColorsArray.value.length]
      : ApplicationDefaults.colors.LightGray;
  };

  return {
    colorMap: readonly(colorMap),
    setColorMap,
    hoveredItemId,
    setHoveredItemId,
    firstMatrixHoveredItemId: readonly(firstMatrixHoveredItemId),
    setFirstMatrixHoveredItemId,
    secondMatrixHoveredItemId: readonly(secondMatrixHoveredItemId),
    setSecondMatrixHoveredItemId,
    unpin,
    getIsUnpinned,
    pinPortfolioTreeItem,
    pinItemUnderAnalysis,
    pinBaseFund,
    pin,
    pinItemAndClearAllOthers,
    togglePeerGroupNameButton,
    getPinParameters,
    clearPinned,
    strategyCodeToColor,
    itemUnderAnalysisColor,
    isOnlyItemUnderAnalysisPinned,
    getRecursivePinItemParameters,
    getUnpinSidebarRowChanges,
    allPinnedColorsArray,
    getNextPinColorByOffset,
  };
}
